import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Accordion, Container, Row } from "react-bootstrap";
import { Box, Stack, Button, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_GroupingState,
  type MRT_ColumnOrderState,
  type MRT_ColumnPinningState,
} from "material-react-table";
import { getTasAuditReport, TasAuditReportRow } from "./Tasupdatelogapi";

const parseSummary = (summary: string[]) =>
  summary
    .filter((s) => s && s.includes(":"))
    .map((s) => {
      const [label, ...rest] = s.split(":");
      return { label: label.trim(), value: rest.join(":").trim() };
    });

const TasUpdateLog = () => {
  const [rows, setRows] = useState<TasAuditReportRow[]>([]);
  const [summary, setSummary] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnPinning, setColumnPinning] = useState<MRT_ColumnPinningState>({ left: [], right: [] });
  const [grouping, setGrouping] = useState<MRT_GroupingState>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await getTasAuditReport();
      if (res?.status) {
        setRows(res.data ?? []);
        setSummary(res.summary ?? []);
        setCount(res.count ?? 0);
        if (res.columns?.length) setColumnOrder(res.columns);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<TasAuditReportRow>[]>(() => [
    { accessorKey: "Trade Date", header: "Trade Date", id: "Trade Date" },
    { accessorKey: "Symbol", header: "Symbol", id: "Symbol" },
    { accessorKey: "Price", header: "Price", id: "Price" },
    { accessorKey: "MTM", header: "MTM", id: "MTM" },
    { accessorKey: "API Price", header: "API Price", id: "API Price" },
    { accessorKey: "Expected Price", header: "Expected Price", id: "Expected Price" },
    { accessorKey: "Trade ID", header: "Trade ID", id: "Trade ID" },
    { accessorKey: "Leg ID", header: "Leg ID", id: "Leg ID" },
  ], []);

  const summaryItems = useMemo(() => parseSummary(summary), [summary]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableColumnActions: true,
    enableColumnResizing: true,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableFilterMatchHighlighting: true,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableRowSelection: false,
    enableEditing: false,
    enableStickyHeader: false,
    enableStickyFooter: false,
    manualPagination: false,
    manualSorting: false,
    manualFiltering: false,
    rowCount: count,
    state: { columnOrder, columnPinning, grouping, isLoading, showAlertBanner: isError, showProgressBars: isLoading },
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: setGrouping,
    muiToolbarAlertBannerProps: isError
      ? { color: "error", children: "Failed to load TAS update log. Please try again." }
      : undefined,
    muiTopToolbarProps: {
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "top-toolbar-wrapper__new",
    },
    muiTableContainerProps: { sx: { backgroundColor: "#fff" }, className: "table-container-wrapper__new" },
    muiTableFooterProps: { sx: { backgroundColor: "#fff" }, className: "table-footer-wrapper__new" },
    muiBottomToolbarProps: { sx: { backgroundColor: "#fff", padding: "8px" }, className: "bottom-toolbar-wrapper__new" },
    muiTableHeadCellProps: { sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal" } },
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0", border: "0" } },

    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>
          Refresh
        </Button>

        {/* ── Summary text items ── */}
        {summaryItems.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.68rem", color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Summary:
            </Typography>
            {summaryItems.map((item, idx) => (
              <React.Fragment key={item.label}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Typography sx={{ fontSize: "0.68rem", color: "#888", textTransform: "uppercase", letterSpacing: 0.3 }}>
                    {item.label.replace(/_/g, " ")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#111827" }}>
                    {item.value}
                  </Typography>
                </Box>
                {idx < summaryItems.length - 1 && (
                  <Typography sx={{ fontSize: "0.6rem", color: "#ddd" }}>|</Typography>
                )}
              </React.Fragment>
            ))}
            <Typography sx={{ fontSize: "0.6rem", color: "#ddd" }}>|</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography sx={{ fontSize: "0.68rem", color: "#888", textTransform: "uppercase", letterSpacing: 0.3 }}>
                Count
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#111827" }}>
                {count}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    ),
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Tas Update Log
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="table-ad__wrapper">
                      <div className="table-ad__wrapperCard">
                        <MaterialReactTable table={table} />
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default TasUpdateLog;